import { FormEvent, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  Alert,
  Autocomplete,
  Button,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { STATES } from '../../constants';
import { surepassTheme } from '../../theme/surepass';
import type { WebApiResult } from '../../networkRequest/api';
import type { Location, LocationFields } from '../../networkRequest/locationsService';

const TITLE_ID = 'location-dialog-title';

/**
 * The dialog paper renders as the form. `noValidate` turns off the browser's own
 * checks, so a `required` or `pattern` attribute can never block the submit with
 * the browser's bubble before our messages show. MUI's paper slot types don't
 * list form attributes, so they're passed this way.
 */
const FORM_PAPER_PROPS = { component: 'form', noValidate: true } as const;

/** Two fields side by side from md up, stacked below it (DESIGN.md: snap on md). */
const HALF_WIDTH = { flex: { md: '1 1 0' }, minWidth: 0 };

const ZIP_PATTERN = /^\d{5}$/;

type LocationFormValues = {
  name: string;
  description: string;
  street_address_1: string;
  street_address_2: string;
  city: string;
  state: string | null;
  postal: string;
};

type LocationField = keyof LocationFormValues;
type RequiredField = 'name' | 'street_address_1' | 'city' | 'state' | 'postal';

/** In the order they appear, so a failed save can focus the first one with an error. */
const FIELDS: LocationField[] = [
  'name',
  'description',
  'street_address_1',
  'street_address_2',
  'city',
  'state',
  'postal',
];
const REQUIRED_FIELDS: RequiredField[] = ['name', 'street_address_1', 'city', 'state', 'postal'];

/** The input's id, so a failed save can find it to focus. */
const getFieldId = (field: LocationField) => `location-${field}`;

/** An empty string means the field is valid. */
type LocationFormErrors = Partial<Record<LocationField, string>>;

const getFormValues = (location: Location | null): LocationFormValues => ({
  name: location?.name ?? '',
  description: location?.description ?? '',
  street_address_1: location?.street_address_1 ?? '',
  street_address_2: location?.street_address_2 ?? '',
  city: location?.city ?? '',
  state: location?.state ?? null,
  postal: location?.postal ?? '',
});

/** What's sent to the API: trimmed, with an empty optional field sent as null. */
const getLocationFields = (values: LocationFormValues): LocationFields => ({
  name: values.name.trim(),
  description: values.description.trim() || null,
  street_address_1: values.street_address_1.trim(),
  street_address_2: values.street_address_2.trim() || null,
  city: values.city.trim(),
  state: values.state ?? '',
  postal: values.postal.trim(),
});

/** What's wrong with a required field and what to do about it (TextField page), or '' if nothing. */
const getFieldError = (field: RequiredField, values: LocationFormValues): string => {
  const value = (values[field] ?? '').trim();
  let error = '';
  if (field === 'name' && !value) {
    error = 'Enter a name for this location.';
  } else if (field === 'street_address_1' && !value) {
    error = 'Enter the street address.';
  } else if (field === 'city' && !value) {
    error = 'Enter the city.';
  } else if (field === 'state' && !value) {
    error = 'Choose a state from the list.';
  } else if (field === 'postal' && !value) {
    error = 'Enter the zip code.';
  } else if (field === 'postal' && !ZIP_PATTERN.test(value)) {
    error = 'Enter a 5-digit zip code.';
  }
  return error;
};

type LocationDialogProps = {
  open: boolean;
  /** The location being edited, or null to add a new one. */
  location: Location | null;
  /** Sends the location to the API. The dialog closes when it succeeds. */
  onSave: (fields: LocationFields) => Promise<WebApiResult<Location>>;
  onClose: () => void;
};

/**
 * Add or edit a location. Fields are validated when they lose focus and again on
 * save (TextField page: on blur or submit, never per keystroke). The API's own
 * errors go under the field they're about, or above the buttons if they're about
 * no field.
 *
 * The form starts from `location` when it mounts, so the screen gives this a new
 * `key` each time it opens.
 */
const LocationDialog = ({ open, location, onSave, onClose }: LocationDialogProps) => {
  const [values, setValues] = useState(() => getFormValues(location));
  const [errors, setErrors] = useState<LocationFormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  // An error from the API that isn't about one field.
  const [saveError, setSaveError] = useState('');
  // Below sm the dialog fills the screen (Dialog page). `fullScreen` is a prop, so it needs the hook.
  const isMobile = useMediaQuery(surepassTheme.breakpoints.down('sm'));

  let title = 'Add Location';
  let saveLabel = 'Add Location';
  if (location) {
    title = 'Edit Location';
    saveLabel = 'Save Changes';
  }

  const updateValue = (field: LocationField, value: string | null) => {
    setValues({ ...values, [field]: value });
  };

  const validateField = (field: RequiredField) => {
    setErrors({ ...errors, [field]: getFieldError(field, values) });
  };

  /** Shows the errors and moves focus to the first field with one. */
  const showFieldErrors = (nextErrors: LocationFormErrors) => {
    const firstInvalidField = FIELDS.find((field) => nextErrors[field]);
    // Render the errors before moving focus, so the field is announced with its message.
    flushSync(() => setErrors(nextErrors));
    if (firstInvalidField) {
      document.getElementById(getFieldId(firstInvalidField))?.focus();
    }
  };

  const save = async () => {
    setIsSaving(true);
    setSaveError('');
    const result = await onSave(getLocationFields(values));
    setIsSaving(false);
    if (result.error) {
      const fieldErrors: LocationFormErrors = {};
      const otherMessages: string[] = [];
      (result.errors ?? []).forEach((apiError) => {
        const field = FIELDS.find((name) => name === apiError.attribute);
        if (field) {
          fieldErrors[field] = apiError.message;
        } else {
          otherMessages.push(apiError.message);
        }
      });
      // No error objects at all (e.g. the network failed): the joined message is all there is.
      if (!result.errors?.length) {
        otherMessages.push(result.error);
      }
      setSaveError(otherMessages.join(' '));
      showFieldErrors(fieldErrors);
    } else {
      onClose();
    }
  };

  // Save stays enabled and says what's missing when clicked (Button page).
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const nextErrors: LocationFormErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      nextErrors[field] = getFieldError(field, values);
    });
    if (REQUIRED_FIELDS.some((field) => nextErrors[field])) {
      showFieldErrors(nextErrors);
    } else {
      save();
    }
  };

  // Nothing closes the dialog while a save is in flight, so its result isn't lost.
  const close = () => {
    if (!isSaving) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      aria-labelledby={TITLE_ID}
      // The paper is the form, so Enter in any field submits and the content keeps its own scrolling.
      slotProps={{ paper: { ...FORM_PAPER_PROPS, onSubmit: handleSubmit } }}
    >
      <CardHeader
        title={title}
        slotProps={{
          title: { id: TITLE_ID, component: 'h2' },
          // Cancels MUI's negative margin on the action slot, so the header is 72px.
          action: { sx: { alignSelf: 'center', m: 0 } },
        }}
        action={
          // Tooltip page: a control that can be disabled is wrapped in a span, or the
          // tooltip can't open on it and MUI warns.
          <Tooltip title="Close" describeChild>
            <span>
              <IconButton aria-label="Close" onClick={close} disabled={isSaving}>
                <CloseIcon />
              </IconButton>
            </span>
          </Tooltip>
        }
      />

      {/* TextField page: a mostly-required form marks the optional fields instead of the required ones.
          The team chose "(optional)" in the label rather than helper text below the field. */}
      <DialogContent sx={{ p: 2 }}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            id={getFieldId('name')}
            label="Name"
            value={values.name}
            onChange={(event) => updateValue('name', event.target.value)}
            onBlur={() => validateField('name')}
            error={Boolean(errors.name)}
            helperText={errors.name}
            autoComplete="off"
            fullWidth
          />
          <TextField
            id={getFieldId('description')}
            label="Description (optional)"
            value={values.description}
            onChange={(event) => updateValue('description', event.target.value)}
            error={Boolean(errors.description)}
            helperText={errors.description}
            autoComplete="off"
            fullWidth
          />

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              id={getFieldId('street_address_1')}
              label="Street address 1"
              value={values.street_address_1}
              onChange={(event) => updateValue('street_address_1', event.target.value)}
              onBlur={() => validateField('street_address_1')}
              error={Boolean(errors.street_address_1)}
              helperText={errors.street_address_1}
              autoComplete="address-line1"
              fullWidth
              sx={HALF_WIDTH}
            />
            <TextField
              id={getFieldId('street_address_2')}
              label="Street address 2 (optional)"
              value={values.street_address_2}
              onChange={(event) => updateValue('street_address_2', event.target.value)}
              error={Boolean(errors.street_address_2)}
              helperText={errors.street_address_2}
              autoComplete="address-line2"
              fullWidth
              sx={HALF_WIDTH}
            />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              id={getFieldId('city')}
              label="City"
              value={values.city}
              onChange={(event) => updateValue('city', event.target.value)}
              onBlur={() => validateField('city')}
              error={Boolean(errors.city)}
              helperText={errors.city}
              autoComplete="address-level2"
              fullWidth
              sx={HALF_WIDTH}
            />
            {/* Not freeSolo, so typed text that matches no state is cleared on blur
                and the value stays whatever was last picked from the list. */}
            <Autocomplete
              id={getFieldId('state')}
              options={STATES}
              value={values.state}
              onChange={(_, state) => updateValue('state', state)}
              autoHighlight
              fullWidth
              sx={HALF_WIDTH}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="State"
                  placeholder="Start typing a state"
                  onBlur={() => validateField('state')}
                  error={Boolean(errors.state)}
                  helperText={errors.state}
                />
              )}
            />
          </Stack>

          <TextField
            id={getFieldId('postal')}
            label="Zip code"
            value={values.postal}
            onChange={(event) => updateValue('postal', event.target.value)}
            onBlur={() => validateField('postal')}
            error={Boolean(errors.postal)}
            helperText={errors.postal}
            autoComplete="postal-code"
            fullWidth
            slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 5 } }}
          />
        </Stack>
      </DialogContent>

      {/* Above the buttons rather than at the top, so it's seen however far the form is scrolled. */}
      {saveError && (
        <Alert severity="error" sx={{ mx: 2 }}>
          {saveError}
        </Alert>
      )}

      {/* Secondary then primary, right-aligned; equal width on mobile (Dialog page). */}
      <DialogActions sx={{ p: 2, gap: 2 }}>
        <Button
          color="inherit"
          onClick={close}
          disabled={isSaving}
          sx={{ flex: { xs: 1, sm: 'initial' } }}
        >
          Cancel
        </Button>
        {/* Button page: an in-flight action shows `loading`. */}
        <Button
          type="submit"
          variant="contained"
          loading={isSaving}
          sx={{ flex: { xs: 1, sm: 'initial' } }}
        >
          {saveLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationDialog;
